import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { companyInfo, services } from '../../data/mock';

const Footer = () => {
  const navigate = useNavigate();

  const handleManageCookies = () => {
    localStorage.removeItem('cookieConsent');
    window.location.reload();
  };

  const handleScrollToSection = (path, sectionId) => {
    // Navigate to the page first
    navigate(path);
    
    // Wait for navigation to complete, then scroll to section
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // If no section ID, scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_parquet-expert-1/artifacts/idfklky9_av..png" 
                alt="G&B Wood - Parquet & Legno" 
                className="h-24 w-auto object-contain"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              Specialisti nella posa parquet e lavori in legno su misura. 
              Oltre 30 anni di esperienza al servizio della qualità.
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">I Nostri Servizi</h3>
            <ul className="space-y-3">
              {services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/servizi#${service.id}`}
                    className="text-slate-400 hover:text-amber-400 text-sm transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Link Utili</h3>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => handleScrollToSection('/', null)}
                  className="text-slate-400 hover:text-amber-400 text-sm transition-colors text-left"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleScrollToSection('/chi-siamo', null)}
                  className="text-slate-400 hover:text-amber-400 text-sm transition-colors text-left"
                >
                  Chi Siamo
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleScrollToSection('/portfolio', null)}
                  className="text-slate-400 hover:text-amber-400 text-sm transition-colors text-left"
                >
                  Portfolio
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleScrollToSection('/contatti', 'contatti-section')}
                  className="text-slate-400 hover:text-amber-400 text-sm transition-colors text-left"
                >
                  Contatti
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleScrollToSection('/preventivi', 'preventivo-section')}
                  className="text-slate-400 hover:text-amber-400 text-sm transition-colors text-left"
                >
                  Richiedi Preventivo
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contatti</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">{companyInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} className="text-slate-400 hover:text-amber-400 text-sm transition-colors">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-slate-400 hover:text-amber-400 text-sm transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">{companyInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Links */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} G&B Wood di Carmine Avilio. Tutti i diritti riservati.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/privacy-policy" className="text-slate-500 hover:text-amber-400 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-slate-500 hover:text-amber-400 text-sm transition-colors">
                Cookie Policy
              </Link>
              <Link to="/termini-condizioni" className="text-slate-500 hover:text-amber-400 text-sm transition-colors">
                Termini e Condizioni
              </Link>
              <Link to="/disclaimer" className="text-slate-500 hover:text-amber-400 text-sm transition-colors">
                Disclaimer
              </Link>
              <button 
                onClick={handleManageCookies}
                className="text-slate-500 hover:text-amber-400 text-sm transition-colors"
              >
                Gestione Cookie
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
